<template>
  <q-tab-pane name="security">
    <!-- Password -->
    <div class="row gutter-x-xl">
      <div class="col-12 col-md-3">
        <h6 class="q-mt-md q-mb-sm q-title">{{ $t('change_password') }}</h6>

        <p class="text-faded q-ma-none">{{ $t('change_password_hint') }}</p>
      </div>

      <div class="col-12 col-md-9">
        <form @submit.prevent="handleSaveChanges">
          <q-field
            :error="errors.has('password')"
            :error-label="errors.first('password')"
          >
            <q-input
              v-model="password"
              v-validate.disable="'required|min:8'"
              type="password"
              name="password"
              :float-label="$t('password')"
            />
          </q-field>

          <q-field
            :error="errors.has('password_confirmation')"
            :error-label="errors.first('password_confirmation')"
          >
            <q-input
              v-model="password_confirmation"
              type="password"
              name="passwordConfirmation"
              :float-label="$t('password_confirmation')"
            />
          </q-field>

          <div class="row justify-end">
            <q-btn type="submit" color="primary">{{ $t('save') }}</q-btn>
          </div>
        </form>
      </div>
    </div>

    <hr/>

    <!-- 2FA -->
    <div class="q-mt-md">
      <h6 class="q-mb-sm q-title">{{ $t('tfa') }}</h6>

      <p v-html="$t('otp_hint')" class="text-faded"></p>

      <p class="text-faded">
        <span class="text-bold">{{ $t('status') }}:</span>&nbsp;<span :class="tfaTextClass">{{ tfaStatusText }}</span>
      </p>

      <q-btn color="white" text-color="black" @click="handleDisable2FA" v-if="user.two_factor_enabled">
        {{ $t('disable') }}
      </q-btn>
      <q-btn color="white" text-color="black" @click="handleEnable2FA" v-if="!user.two_factor_enabled">
        {{ $t('enable') }}
      </q-btn>

      <q-btn color="primary" flat class="on-right" @click="handleBackupCodes" v-if="user.two_factor_enabled">
        {{ $t('backup_codes') }}
      </q-btn>
    </div>

    <tfa-modal ref="tfaModal"></tfa-modal>
    <backup-codes-modal ref="backupCodesModal"></backup-codes-modal>

    <authorization-dialog ref="authorizationDialog"></authorization-dialog>
  </q-tab-pane>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import AuthorizationDialog from 'components/Common/AuthorizationDialog.vue'
  import BackupCodesModal from 'components/Settings/BackupCodesModal.vue'
  import TfaModal from 'components/Settings/TfaModal.vue'
  import { UserClient } from 'src/api/Users'
  import { createErrorFromResponse } from 'src/utils/error'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        password: null,
        password_confirmation: null
      }
    },

    computed: {
      ...mapGetters(['user']),

      tfaStatus () {
        return this.user.two_factor_enabled
      },

      tfaStatusText () {
        return this.tfaStatus ? this.$t('enabled') : this.$t('disabled')
      },

      tfaTextClass () {
        return this.tfaStatus ? 'text-positive' : 'text-negative'
      }
    },

    methods: {
      ...mapActions(['set2FAState', 'logout', 'updateUser']),

      handleSaveChanges () {
        this.errors.clear()

        this.$validator.validateAll()
          .then(result => {
            if (!result) {
              return
            }

            if (this.password !== this.password_confirmation) {
              this.errors.add({
                field: 'password_confirmation',
                msg: this.$t('password_confirmation_invalid')
              })

              return
            }

            return this.$refs.authorizationDialog.open()
              .then(authorizationPassword => {
                this.saveChanges(authorizationPassword)
              })
          })
      },

      saveChanges (authorizationPassword = null) {
        const data = {
          password: this.password,
          confirmation_password: authorizationPassword
        }

        this.updateUser(data)
          .then(() => {
            this.logout()
            this.$router.push('/login')

            return this.$q.notify({ message: this.$t('RECENT_PASSWORD_CHANGE'), timeout: 5000, type: 'info' })
          })
          .catch(createErrorFromResponse)
      },

      handleEnable2FA () {
        this.$refs.tfaModal.open()
      },

      handleDisable2FA () {
        this.$refs.authorizationDialog.open()
          .then(authorizationPassword => {
            return new UserClient()
              .disable2FA(authorizationPassword)
          })
          .then(() => {
            this.set2FAState(false)

            this.$q.notify({ message: this.$t('tfa_disabled'), type: 'positive' })
          })
          .catch(createErrorFromResponse)
      },

      handleBackupCodes () {
        this.$refs.authorizationDialog.open()
          .then(authorizationPassword => {
            this.$refs.backupCodesModal.open(authorizationPassword)
          })
      }
    },

    components: {
      AuthorizationDialog,
      BackupCodesModal,
      TfaModal
    }
  }
</script>
